"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Mic, MessageSquare, Activity, ListTodo, Bot, User, PhoneCall, Sparkles, CircleCheck, Clock, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const transcriptMessages = [{
  id: 1,
  role: "agent",
  text: "Hola, gracias por comunicarse con AVTECSA. Mi nombre es Carlos, ¿en qué le puedo ayudar hoy?",
  time: "10:00 AM"
}, {
  id: 2,
  role: "customer",
  text: "Hola Carlos. Estoy buscando información sobre cómo integrar Microsoft Teams con nuestra centralita actual.",
  time: "10:01 AM"
}, {
  id: 3,
  role: "agent",
  text: "Por supuesto, es una de nuestras especialidades. Nuestro sistema PBX-Vtec se integra de forma nativa. ¿Qué marca de centralita utilizan actualmente y cuántos usuarios tienen?",
  time: "10:01 AM"
}, {
  id: 4,
  role: "customer",
  text: "Tenemos una Grandstream y somos aproximadamente 50 usuarios en total. Nos interesa mucho la funcionalidad omnicanal.",
  time: "10:02 AM"
}, {
  id: 5,
  role: "agent",
  text: "Excelente. Con Grandstream podemos hacer una integración transparente. Además, el módulo omnicanal les permitirá gestionar WhatsApp y llamadas desde la misma interfaz de Teams.",
  time: "10:03 AM"
}, {
  id: 6,
  role: "customer",
  text: "Suena exactamente a lo que necesitamos. ¿Sería posible agendar una demostración técnica para mi equipo?",
  time: "10:04 AM"
}];
const analysisTopics = [{
  id: 1,
  name: "Integración MS Teams",
  type: "primary"
}, {
  id: 2,
  name: "PBX-Vtec",
  type: "secondary"
}, {
  id: 3,
  name: "Grandstream",
  type: "secondary"
}, {
  id: 4,
  name: "Omnicanal",
  type: "primary"
}];
const actionItems = [{
  id: 1,
  task: "Agendar demostración técnica",
  status: "pending"
}, {
  id: 2,
  task: "Enviar cotización para 50 usuarios",
  status: "pending"
}, {
  id: 3,
  task: "Verificar compatibilidad modelo Grandstream",
  status: "completed"
}];
const benefits = [{
  id: 1,
  title: "Transcripción en Tiempo Real",
  desc: "Convierte voz a texto instantáneamente con alta precisión impulsada por IA, facilitando la revisión y el control de calidad.",
  icon: Mic
}, {
  id: 2,
  title: "Análisis de Sentimiento",
  desc: "Detecta la emoción del cliente durante la interacción para guiar a los agentes y mejorar la satisfacción general.",
  icon: Activity
}, {
  id: 3,
  title: "Extracción de Tareas",
  desc: "Identifica automáticamente compromisos, recordatorios y próximos pasos mencionados durante la llamada.",
  icon: ListTodo
}, {
  id: 4,
  title: "Cobertura Omnicanal",
  desc: "Analiza interacciones de voz, chats de WhatsApp y SMS en un panel unificado para una visión 360 del cliente.",
  icon: MessageSquare
}];
export default function AiTranscriptionAnalysis() {
  return <section id="aitranscriptionanalysis" className="py-14 md:py-16 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[10%] -left-[10%] w-[40%] h-[40%] rounded-lg bg-secondary/5 blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        
        {}
        <div className="text-center max-w-4xl mx-auto mb-8 md:mb-10 space-y-6">
          <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 px-4 py-1.5 text-sm">
            <Sparkles className="w-4 h-4 mr-2" />
            Inteligencia Artificial Aplicada
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold font-default text-foreground leading-tight tracking-tight">
            Mejora cada interacción con nuestro <span className="text-primary">análisis de llamadas basado en IA</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Obtenga información valiosa y aumente la eficiencia de sus canales de atención, llamadas, call center y chat omnicanal. Transforme conversaciones en datos accionables.
          </p>
        </div>

        {}
        <div className="mb-8 md:mb-10 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-xl shadow-2xl overflow-hidden flex flex-col">
          {}
          <div className="bg-muted/50 border-b border-border/50 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-lg bg-destructive/80" />
                <div className="w-3 h-3 rounded-lg bg-chart-4/80" />
                <div className="w-3 h-3 rounded-lg bg-chart-3/80" />
              </div>
              <Separator orientation="vertical" className="h-4 mx-2" />
              <div className="flex items-center text-sm text-muted-foreground font-medium">
                <PhoneCall className="w-4 h-4 mr-2 text-primary" />
                Llamada en curso - Soporte Técnico
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="bg-chart-3/10 text-chart-3 border-chart-3/20 animate-pulse">
                Grabando & Analizando
              </Badge>
              <span className="text-sm font-mono text-muted-foreground">04:23</span>
            </div>
          </div>

          {}
          <div className="grid grid-cols-1 lg:grid-cols-3 h-[600px] divide-y lg:divide-y-0 lg:divide-x divide-border/50">
            
            {}
            <div className="col-span-1 lg:col-span-2 flex flex-col bg-background/50">
              <div className="p-4 border-b border-border/50 flex justify-between items-center bg-card/30">
                <h3 className="font-semibold font-default flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  Transcripción en Vivo
                </h3>
                <Button variant="outline" size="sm" className="h-8 text-xs">
                  Exportar PDF
                </Button>
              </div>
              
              <ScrollArea className="flex-1 p-6">
                <div className="space-y-6">
                  {transcriptMessages.map((msg, i) => <div key={msg.id} data-index={i} className={`flex gap-4 ${msg.role === 'agent' ? '' : 'flex-row-reverse'}`}>
                      <SafeHydrate><Avatar className={`w-8 h-8 border ${msg.role === 'agent' ? 'border-primary/20' : 'border-border'}`}>
                        <AvatarFallback className={msg.role === 'agent' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}>
                          {msg.role === 'agent' ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                      <div className={`flex flex-col gap-1 max-w-[80%] ${msg.role === 'agent' ? 'items-start' : 'items-end'}`}>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{msg.role === 'agent' ? 'Agente (Carlos)' : 'Cliente'}</span>
                          <span>{msg.time}</span>
                        </div>
                        <div className={`p-3 rounded-2xl text-sm shadow-sm ${msg.role === 'agent' ? 'bg-card border border-border/50 rounded-tl-none' : 'bg-primary text-primary-foreground rounded-tr-none'}`}>
                          {msg.text}
                        </div>
                      </div>
                    </div>)}
                  <div className="flex items-center gap-2 text-muted-foreground text-sm p-2">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-lg bg-primary/60 animate-bounce" style={{
                      animationDelay: '0ms'
                    }} />
                      <span className="w-1.5 h-1.5 rounded-lg bg-primary/60 animate-bounce" style={{
                      animationDelay: '150ms'
                    }} />
                      <span className="w-1.5 h-1.5 rounded-lg bg-primary/60 animate-bounce" style={{
                      animationDelay: '300ms'
                    }} />
                    </div>
                    <span className="italic text-xs">Cliente escribiendo...</span>
                  </div>
                </div>
              </ScrollArea>
            </div>

            {}
            <div className="col-span-1 flex flex-col bg-muted/10">
              <div className="p-4 border-b border-border/50 bg-card/30">
                <h3 className="font-semibold font-default flex items-center gap-2">
                  <Activity className="w-5 h-5 text-secondary" />
                  Análisis de la Conversación
                </h3>
              </div>
              
              <ScrollArea className="flex-1">
                <div className="p-6 space-y-8">
                  
                  {}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="text-sm font-medium text-foreground">Sentimiento del Cliente</h4>
                      <span className="text-sm font-bold text-chart-3">Positivo (85%)</span>
                    </div>
                    <Progress value={85} className="h-2 bg-muted" />
                    <p className="text-xs text-muted-foreground">
                      El cliente muestra alto interés y receptividad hacia la solución propuesta.
                    </p>
                  </div>

                  <Separator className="bg-border/50" />

                  {}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-foreground">Temas Clave Detectados</h4>
                    <div className="flex flex-wrap gap-2">
                      {analysisTopics.map((topic, i) => <Badge key={topic.id} data-index={i} variant={topic.type === 'primary' ? 'default' : 'secondary'} className={topic.type === 'primary' ? 'bg-primary/10 text-primary hover:bg-primary/20 border-primary/20' : ''}>
                          {topic.name}
                        </Badge>)}
                    </div>
                  </div>

                  <Separator className="bg-border/50" />

                  {}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="text-sm font-medium text-foreground">Tareas Extraídas</h4>
                      <Badge variant="outline" className="text-xs font-mono">3 detectadas</Badge>
                    </div>
                    <ul className="space-y-3">
                      {actionItems.map((item, i) => <li key={item.id} data-index={i} className="flex items-start gap-3 text-sm">
                          {item.status === 'completed' ? <CircleCheck className="w-4 h-4 text-chart-3 mt-0.5 shrink-0" /> : <Clock className="w-4 h-4 text-chart-4 mt-0.5 shrink-0" />}
                          <span className={item.status === 'completed' ? 'text-muted-foreground line-through' : 'text-foreground'}>
                            {item.task}
                          </span>
                        </li>)}
                    </ul>
                  </div>

                </div>
              </ScrollArea>
            </div>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, i) => {
          const Icon = benefit.icon;
          return <Card key={benefit.id} data-index={i} className="bg-card/40 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 group">
                <CardHeader className="pb-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg font-default">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm leading-relaxed">
                    {benefit.desc}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>

        {}
        <div className="mt-8 md:mt-10 text-center">
          <Button asChild size="lg" className="rounded-lg px-8 shadow-md hover:shadow-lg transition-all">
            <Link to="#">
              Explorar Soluciones IA
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}